import React from "react";
import { connect } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import Select from "react-select";

import CsLineIcons from "../../../cs-line-icons";
import ConditionalButton from "../../../layout/conditional-button";

import { isGuid } from "../../../utils/string";

import { get as getAccounts } from "../../../actions/accounts";
import { get as getReportTemplates } from "../../../actions/report.template";
import { get as getSetting, save as saveSetting } from "../../../actions/settings";
import { loading, send as pageInfo } from "../../../actions/page";
import { pack as sendAlert } from "../../../actions/alert";

const EMPTY_ACCOUNTS = {
  general: {
    expense: null,
    from: null,
    check: null,
  },
  payroll: {
    expense: null,
    wagesExpense: null,
    employerTaxExpense: null,
    taxLiability: null,
    deductionLiability: null,
    from: null,
    check: null,
  },
  client: {
    payments: null,
    expenses: null,
    from: null,
  },
};

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const safeParseJson = (value, fallback = {}) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : (value || fallback);
  } catch (_err) {
    return fallback;
  }
};

const mergeAccounts = (value) => ({
  ...clone(EMPTY_ACCOUNTS),
  ...clone(value),
  general: { ...EMPTY_ACCOUNTS.general, ...(value?.general || {}) },
  payroll: { ...EMPTY_ACCOUNTS.payroll, ...(value?.payroll || {}) },
  client: { ...EMPTY_ACCOUNTS.client, ...(value?.client || {}) },
});

class AccountingSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      setting: null,
      accounts: clone(EMPTY_ACCOUNTS),
      chartAccounts: [],
      reportTemplates: [],
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Accounting Settings", breadcrumb: "Accounting" });
    await this.loadData();
  };

  loadData = async () => {
    this.props.loading(true);
    try {
      const [setting, chartAccounts, reportTemplates] = await Promise.all([
        this.props.getSetting({ authentic: true, name: "ACCOUNTS" }),
        this.props.getAccounts(null),
        this.props.getReportTemplates(null),
      ]);

      this.setState({
        setting,
        accounts: mergeAccounts(safeParseJson(setting?.value, EMPTY_ACCOUNTS)),
        chartAccounts: chartAccounts || [],
        reportTemplates: reportTemplates || [],
      });
    } finally {
      this.props.loading(false);
    }
  };

  accountLabel = (account) => {
    const number = String(account?.accountNumber || "").trim();
    const name = String(account?.name || "").trim();
    if (number && name) return `${number} - ${name}`;
    return name || number || account?.uuid || "";
  };

  accountOptions = (filter) => (this.state.chartAccounts || [])
    .filter((account) => account?.active !== false)
    .filter(filter || (() => true))
    .map((account) => ({
      value: account.uuid,
      label: this.accountLabel(account),
    }));

  reportOptions = (selectedUuid) => {
    const templates = this.state.reportTemplates || [];
    const selected = templates.find((template) => template.uuid === selectedUuid);
    const filtered = templates.filter((template) => String(template?.category || "").toLowerCase() === "check");
    const options = (filtered.length ? filtered : templates).map((template) => ({
      value: template.uuid,
      label: template.caption || template.name || template.uuid,
    }));

    if (selected && !options.some((option) => option.value === selected.uuid)) {
      options.unshift({
        value: selected.uuid,
        label: selected.caption || selected.name || selected.uuid,
      });
    }

    return options;
  };

  selectedOption = (value, options) => options.find((option) => option.value === value) || null;

  renderSelectLabel = ({ label, href }) => (
    <Form.Label>
      {label}
      {href && (
        <a
          href={href}
          className="ms-1 text-muted"
          title="Open selected item"
          onClick={(event) => event.stopPropagation()}
        >
          <CsLineIcons icon="arrow-bottom-left" size="10" />
        </a>
      )}
    </Form.Label>
  );

  onFieldChange = (section, field) => (option) => {
    this.setState((prev) => ({
      accounts: {
        ...prev.accounts,
        [section]: {
          ...(prev.accounts?.[section] || {}),
          [field]: option?.value || null,
        },
      },
    }));
  };

  renderAccountSelect = ({ section, field, label, filter, placeholder }) => {
    const value = this.state.accounts?.[section]?.[field] || null;
    const options = this.accountOptions(filter);

    return (
      <div className="mb-3 top-label">
        <Select
          inputId={`${section}.${field}`}
          classNamePrefix="react-select"
          isClearable
          placeholder={placeholder || "(Select account)"}
          value={this.selectedOption(value, options)}
          options={options}
          onChange={this.onFieldChange(section, field)}
        />
        {this.renderSelectLabel({
          label,
          href: isGuid(value) ? `/dashboard/accounting/accounts/${value}` : null,
        })}
      </div>
    );
  };

  renderCheckSelect = ({ section, field, label }) => {
    const value = this.state.accounts?.[section]?.[field] || null;
    const options = this.reportOptions(value);

    return (
      <div className="mb-3 top-label">
        <Select
          inputId={`${section}.${field}`}
          classNamePrefix="react-select"
          isClearable
          placeholder="(Select check template)"
          value={this.selectedOption(value, options)}
          options={options}
          onChange={this.onFieldChange(section, field)}
        />
        {this.renderSelectLabel({
          label,
          href: isGuid(value) ? `/dashboard/templates/report/${value}` : null,
        })}
      </div>
    );
  };

  cleanAccounts = () => {
    const accounts = mergeAccounts(this.state.accounts);
    Object.keys(accounts).forEach((section) => {
      Object.keys(accounts[section] || {}).forEach((field) => {
        if (!isGuid(accounts[section][field])) accounts[section][field] = null;
      });
    });
    return accounts;
  };

  onSaveClick = async () => {
    const accounts = this.cleanAccounts();

    this.props.loading(true);
    try {
      const result = await this.props.saveSetting({
        uuid: this.state.setting?.uuid,
        name: "ACCOUNTS",
        category: "Accounting",
        description: "Accounting account and check template configuration",
        visibility: this.state.setting?.visibility ?? 3,
        priority: this.state.setting?.priority ?? 0,
        value: JSON.stringify(accounts),
      });

      this.setState({
        setting: result,
        accounts,
      });
      this.props.sendAlert(["Accounting settings saved."], "success");
    } finally {
      this.props.loading(false);
    }
  };

  render() {
    const isCashAccount = (account) =>
      account?.type === "ASSET"
      || ["OPERATING_CASH", "IOLTA_TRUST_CASH"].includes(account?.purpose);
    const isPayrollExpense = (account) => ["EXPENSE", "COGS"].includes(account?.type);
    const isClientPaymentAccount = (account) =>
      account?.type === "LIABILITY" || account?.purpose === "CLIENT_TRUST_LIABILITY";
    const isClientExpenseAccount = (account) => ["ASSET", "EXPENSE", "COGS"].includes(account?.type);

    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Save Accounting Settings"
            display
            enabled
            onEnabledClick={this.onSaveClick}
            icon="save"
          >
            Save
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row className="g-3">
            <Col xs="12">
              <h2 className="small-title">General</h2>
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "general",
                field: "expense",
                label: "BUSINESS EXPENSE ACCOUNT",
                filter: isPayrollExpense,
                placeholder: "(Select default expense account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "general",
                field: "from",
                label: "BUSINESS EXPENSE PAID FROM ACCOUNT",
                filter: isCashAccount,
                placeholder: "(Select cash account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderCheckSelect({
                section: "general",
                field: "check",
                label: "DEFAULT CHECK TEMPLATE",
              })}
            </Col>
          </Row>

          <Row className="g-3">
            <Col xs="12">
              <h2 className="small-title">Payroll</h2>
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "payroll",
                field: "wagesExpense",
                label: "PAYROLL WAGES EXPENSE ACCOUNT",
                filter: isPayrollExpense,
                placeholder: "(Select wages expense account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "payroll",
                field: "employerTaxExpense",
                label: "EMPLOYER PAYROLL TAX EXPENSE ACCOUNT",
                filter: isPayrollExpense,
                placeholder: "(Select employer tax expense account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "payroll",
                field: "from",
                label: "PAYROLL PAID FROM ACCOUNT",
                filter: isCashAccount,
                placeholder: "(Select cash account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "payroll",
                field: "taxLiability",
                label: "PAYROLL TAX LIABILITY ACCOUNT",
                filter: isClientPaymentAccount,
                placeholder: "(Select payroll tax liability account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "payroll",
                field: "deductionLiability",
                label: "PAYROLL DEDUCTION LIABILITY ACCOUNT",
                filter: isClientPaymentAccount,
                placeholder: "(Select payroll deduction liability account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderCheckSelect({
                section: "payroll",
                field: "check",
                label: "PAYROLL CHECK TEMPLATE",
              })}
            </Col>
          </Row>

          <Row className="g-3">
            <Col xs="12">
              <h2 className="small-title">Client Billing</h2>
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "client",
                field: "payments",
                label: "CLIENT PAYMENT ACCOUNT",
                filter: isClientPaymentAccount,
                placeholder: "(Select trust liability account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "client",
                field: "expenses",
                label: "CLIENT EXPENSE ACCOUNT",
                filter: isClientExpenseAccount,
                placeholder: "(Select client cost account)",
              })}
            </Col>
            <Col xs="12" md="8" xl="5">
              {this.renderAccountSelect({
                section: "client",
                field: "from",
                label: "CLIENT EXPENSE PAID FROM ACCOUNT",
                filter: isCashAccount,
                placeholder: "(Select cash account)",
              })}
            </Col>
          </Row>
        </Col>
      </>
    );
  }
}

AccountingSettings.propTypes = {
  loading: PropTypes.func.isRequired,
  pageInfo: PropTypes.func.isRequired,
  getAccounts: PropTypes.func.isRequired,
  getReportTemplates: PropTypes.func.isRequired,
  getSetting: PropTypes.func.isRequired,
  saveSetting: PropTypes.func.isRequired,
  sendAlert: PropTypes.func.isRequired,
};

export default connect(null, {
  loading,
  pageInfo,
  getAccounts,
  getReportTemplates,
  getSetting,
  saveSetting,
  sendAlert,
})(AccountingSettings);
